import {
  RESOURCE_FIELD_NAMES,
  TRIGGER_METADATA,
  WORKFLOW_TRIGGERS,
} from 'modules/workflows/constants';
import { SearchQueryField, StartNodeData } from 'modules/workflows/types';

const getTriggerMetadata = ({ nodeClass }: StartNodeData) => TRIGGER_METADATA[nodeClass];

/**
 * Check if user filled out at least first rule
 * Backend will return an error if not
 */
const shouldAddCombo = (nodeData: StartNodeData) => {
  const searchQueries = (nodeData?.searchCombo?.searchQueries ?? []) as SearchQueryField[];
  if (!searchQueries?.length) return false;
  /**
   * this check is added for scheduled journey since it has a different structure than the
   * event & api triggered journey it has a searchCombo rather than searchQuery in the backend
   * so we are bypassing the last check if it was a scheduled journey
   */
  if (nodeData.nodeClass === WORKFLOW_TRIGGERS.SCHEDULED_RECURRING_LIST) return true;
  return searchQueries[0].field && searchQueries[0].comparatorType;
};

/**
 * Because our current `updateNodeData` action combines the passed in nodeData with
 * whatever's saved in the redux store, we need to reset any fields
 * that are not required by the current trigger.
 * @returns {object} An object with required node data fields for current trigger
 * */
export const parseNodeDataForSaving = (
  nodeData: StartNodeData,
  scheduleNodeData: Partial<StartNodeData>,
  isJourneyNewUserEntrySourceEnabled = false,
): Partial<StartNodeData> => {
  const hasCombo = shouldAddCombo(nodeData);
  const { resourceFieldName } = getTriggerMetadata(nodeData) || {};
  const updatedNodeData = { ...nodeData, ...scheduleNodeData };

  // Unset any of the fields that are not the resource field
  // NOTE: There is a possibility that future triggers will need these fields
  // for other purposes.  If this is the case, this logic should be revised.
  (RESOURCE_FIELD_NAMES as string[]).forEach((f) => {
    if (f !== resourceFieldName) updatedNodeData[f] = undefined;
  });

  if (nodeData.nodeClass === WORKFLOW_TRIGGERS.TRIGGERED_CUSTOM_EVENT && !nodeData.eventName) {
    updatedNodeData.eventName = '';
  }

  if ((resourceFieldName && !updatedNodeData[resourceFieldName]) || !resourceFieldName) {
    updatedNodeData.resource = undefined;
  }

  // Unset filters
  if (!hasCombo) {
    updatedNodeData.searchCombo = undefined;
  }

  // Unset any scheduler fields
  if (nodeData.nodeClass !== WORKFLOW_TRIGGERS.SCHEDULED_RECURRING_LIST) {
    Object.keys(scheduleNodeData).forEach((val) => {
      updatedNodeData[val] = undefined;
    });
    updatedNodeData.suppressionListIds = undefined;
  }

  if (
    nodeData.nodeClass !== WORKFLOW_TRIGGERS.FIELD_CHANGED ||
    !isJourneyNewUserEntrySourceEnabled
  ) {
    updatedNodeData.onNewUserCreation = undefined;
  }

  return updatedNodeData;
};
